import { useQueries } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { cctvStreamService, fetchCctvList } from '@/domains/traffic-monitoring/apis/cctv-stream';
import { smartCityApi } from '@/domains/traffic-monitoring/apis/smart-city';
import { VEHICLE_VOLUME_BUCKETS } from '@/domains/traffic-monitoring/constants/dashboard.constants';
import type { EventListRoadType } from '@/domains/traffic-monitoring/constants/smartCity.constant';
import type { CctvListItem } from '@/domains/traffic-monitoring/types/cctv.types';
import type {
  AnomalyTrendData,
  AnomalyTrendView,
  EventCardItem,
  EventListData,
  InfraDetailTypeCode,
  MapMarker,
  MapPoiData,
  SiteWeatherData,
  TrafficVolumeTrendData,
  TrafficVolumeTrendSeries,
  TrafficVolumeTrendView,
  WeatherEntry,
  WeatherView,
} from '@/domains/traffic-monitoring/types/smart-city.types';
import { getWeatherIcon } from '@/domains/traffic-monitoring/utils/weatherIcon';

export interface UseSmartCityDashboardParams {
  /** Scopes the weather panel only. The event list and the two trend charts are
   *  city-wide (all sites) and thus ignore it. */
  siteId?: number;
  date?: string;
  filterAbnormal?: string; // '1h' | '6h' | '12h' | '24h'
  filterVolume?: string; // '1h' | '6h' | '12h' | '24h'
  refetchIntervalMs?: number;
}

const parseHours = (filter?: string): number => {
  if (!filter) return 1;
  const num = parseInt(filter.replace('h', ''), 10);
  return Number.isNaN(num) ? 1 : num;
};

// 'HH:mm' from an ISO timestamp like '2026-07-14T08:15:00+07:00'. Slicing keeps
// the WIB wall-clock the backend already resolved, without a TZ re-interpretation.
const isoTimeLabel = (iso?: string | null): string =>
  iso && iso.length >= 16 ? iso.slice(11, 16) : '';

// --------------------------------------------------------------------------- //
// Normalizers: raw api-dsh envelopes -> view models used by the page.
// --------------------------------------------------------------------------- //

// PSC_PC0202001_BE_003 `infrastructure` layer: POI kind -> the page's
// toggle/icon key. Keyed on the raw code because the dashboard never sends
// `lang` - labels arrive in the session language and would break this mapping
// the moment an operator switches to kor. Values must match SmartCityMaps'
// `iconMapping`, which is an exact-match lookup: 'firestation' is all-lowercase
// there, while the toggle id keeps its 'fireStation' casing (the filter
// lowercases both sides).
const INFRA_TYPE_TO_MARKER: Record<InfraDetailTypeCode, string> = {
  INFRA_DTL_TP_POL: 'policepoi',
  INFRA_DTL_TP_TRN: 'trainpoi',
  INFRA_DTL_TP_GOV: 'governmentpoi',
  INFRA_DTL_TP_FIRE: 'firestation',
  INFRA_DTL_TP_WIFI: 'wifipoi',
  INFRA_DTL_TP_TFL: 'trafficlightpoi',
};

const normalizeMarkers = (data: MapPoiData | null, t: (key: string) => string): MapMarker[] => {
  if (!data?.layers) return [];
  const markers: MapMarker[] = [];
  // Shared by the Ambulance and Owner tooltip lines - owner is derived from
  // the dispatched ambulance, so it has nothing to show whenever this does.
  const ambulanceNotAssigned = t('cmn>monitoring>Ambulance not assigned');

  for (const f of data.layers.health_facility ?? []) {
    if (f.position?.lat == null || f.position?.lon == null) continue;
    const title = f.name || f.type?.label || 'Healthcare';
    const description = f.address || f.type?.label || '';
    markers.push({
      id: `health-${f.id}`,
      type: 'healthcarepoi',
      name: title,
      lat: f.position.lat,
      lng: f.position.lon,
      description,
      tooltip: {
        title,
        description,
        phone: f.contact_number || undefined,
      },
    });
  }

  for (const a of data.layers.ambulance ?? []) {
    if (a.position?.lat == null || a.position?.lon == null) continue; // position from ES may be missing
    markers.push({
      id: `ambulance-${a.id}`,
      type: 'ambulancepoi',
      name: a.police_number || a.serial_no || 'Ambulance',
      lat: a.position.lat,
      lng: a.position.lon,
      description: a.status?.label || a.vehicle_type || '',
    });
  }

  // Looked up by dispatch.ambulance_id below so the incident tooltip can show
  // which vehicle (and its owning facility) is on the case. Only 'ongoing'
  // ambulances are ever in this layer (see the ambulance_status query param),
  // so a dispatch to a vehicle outside that scope falls back to
  // ambulanceNotAssigned too.
  const ambulanceById = new Map((data.layers.ambulance ?? []).map((a) => [a.id, a]));

  for (const i of data.layers.incident ?? []) {
    if (i.position?.lat == null || i.position?.lon == null) continue;
    const assignedAmbulance =
      i.dispatch?.ambulance_id != null ? ambulanceById.get(i.dispatch.ambulance_id) : undefined;
    markers.push({
      // 'Incident' (capitalized) matches SmartCityMaps' iconMapping key; the map
      // toggle filter lowercases before comparing, so it still matches 'incident'.
      id: `incident-${i.id}`,
      type: 'Incident',
      name: i.report_code_id || i.incident_location || 'Incident',
      lat: i.position.lat,
      lng: i.position.lon,
      description: i.service_category?.label || i.incident_location || '',
      tooltip: {
        title: i.report_code_id || i.incident_location || 'Incident',
        description: i.service_category?.label || i.incident_location || '',
        ambulance:
          assignedAmbulance?.police_number || assignedAmbulance?.serial_no || ambulanceNotAssigned,
        owner: assignedAmbulance?.ownership?.label || ambulanceNotAssigned,
      },
    });
  }

  for (const f of data.layers.infrastructure ?? []) {
    if (f.position?.lat == null || f.position?.lon == null) continue;
    const type = INFRA_TYPE_TO_MARKER[f.type?.code as InfraDetailTypeCode];
    // An INFRA_DTL_TP_* code added to cm_code later arrives here unmapped;
    // skip it rather than plot it on the fallback icon.
    if (!type) continue;
    markers.push({
      // ids are unique within a layer only, so every source keeps its prefix.
      id: `infra-${f.id}`,
      type,
      name: f.name || f.type?.label || 'Infrastructure',
      lat: f.position.lat,
      lng: f.position.lon,
      // device_status is null for buildings; the kind label is the useful
      // tooltip line there.
      description: f.device_status?.label || f.type?.label || '',
    });
  }

  return markers;
};

const DEVICE_STATUS_TO_TYPE: Record<string, 'online' | 'offline' | 'maintenance'> = {
  DEV_ST_ON: 'online',
  DEV_ST_OFF: 'offline',
  DEV_ST_MTN: 'maintenance',
};

const normalizeCctvMarkers = (cctvList: CctvListItem[]): MapMarker[] => {
  return cctvList
    .filter((item) => item.latitude != null && item.longitude != null)
    .map((item) => ({
      id: `cctv-${item.cctv_id}`,
      type: 'cctvpoi',
      name: item.name,
      lat: item.latitude as number,
      lng: item.longitude as number,
      description: item.device_status_name || '',
      tooltip: { title: item.name, description: item.device_status_name },
      cctv: {
        name: item.name,
        address: item.address,
        latlong: `${item.latitude}, ${item.longitude}`,
        streamUrl: cctvStreamService.getHlsUrl(item),
        typeVideo: DEVICE_STATUS_TO_TYPE[item.device_status] ?? 'maintenance',
      },
    }));
};

// Dispatch status (AMB_RPT_ST_*) -> badge. A report always carries a status, so
// the fallback only covers the states that have no colour of their own yet.
const REPORT_BADGE: Record<string, EventListRoadType> = {
  AMB_RPT_ST_INC: 'incoming',
  AMB_RPT_ST_ARR: 'arrived',
};

// PSC_PC0201001_BE_003 items are already card-shaped, so this is a near
// pass-through. Anomaly rows get no badge: the minimal payload carries no
// severity, only the dominant event-type label.
const normalizeEvents = (data: EventListData | null): EventCardItem[] => {
  if (!data?.events) return [];
  return data.events.map((e) => ({
    // `id` is unique inside its own `type` only, so the React key is the pair.
    id: `${e.type}:${e.id}`,
    type: e.type,
    title: e.title || 'Event',
    address: e.location || '',
    dateTime: e.timestamp ? e.timestamp.replace('T', ' ').slice(0, 16) : '',
    eventListRoadType:
      e.type === 'report' ? (REPORT_BADGE[e.status?.code ?? ''] ?? 'incoming') : undefined,
    reportId: e.type === 'report' ? e.id : undefined,
    cctvId: e.type === 'event' && e.cctv_id != null ? e.cctv_id : undefined,
    eventId: e.type === 'event' ? e.id : undefined,
  }));
};

const normalizeWeather = (data: SiteWeatherData | null): WeatherView | null => {
  if (!data) return null;
  const current: WeatherEntry | null = data.current;
  return {
    temp_c: current?.temperature_c ?? undefined,
    condition: current?.condition ?? undefined,
    icon: current?.icon ?? undefined,
    humidity_pct: current?.humidity_pct ?? undefined,
    updated_at: isoTimeLabel(current?.timestamp) || undefined,
    forecast: (data.forecast ?? []).map((entry, index) => ({
      id: entry.timestamp ?? index,
      time: isoTimeLabel(entry.timestamp),
      img: getWeatherIcon(entry.icon, entry.condition),
      celcius: entry.temperature_c != null ? `${Math.round(entry.temperature_c)}°` : '--',
    })),
  };
};

const normalizeAnomalyTrend = (data: AnomalyTrendData | null): AnomalyTrendView | null => {
  if (!data?.buckets) return null;
  const times = data.buckets.map((b) => isoTimeLabel(b.timestamp));
  const total_data = data.buckets.map((b) => b.total ?? 0);
  const report_data = data.buckets.map((b) => b.report_total ?? 0);

  const by_type: Record<string, number[]> = {};
  for (const bucket of data.buckets) {
    for (const [code, value] of Object.entries(bucket.by_type ?? {})) {
      const series = by_type[code] ?? [];
      series.push(value ?? 0);
      by_type[code] = series;
    }
  }

  return { times, total_data, by_type, report_data };
};

// Aggregate the 8 raw object-class series into the same 4 display buckets the
// PC0203001 by-vehicle peak-hours chart uses (see VEHICLE_VOLUME_BUCKETS),
// summing member classes element-wise per 15-min bucket.
const toVolumeSeries = (raw: TrafficVolumeTrendSeries[], bucketCount: number) => {
  const byCode = new Map(raw.map((s) => [s.object_class_code, s.data]));
  return VEHICLE_VOLUME_BUCKETS.map((bucket) => ({
    name: bucket.title,
    color: bucket.color,
    data: Array.from({ length: bucketCount }, (_, i) =>
      bucket.classes.reduce((sum, code) => sum + (byCode.get(code)?.[i] ?? 0), 0)
    ),
  }));
};

const normalizeVolumeTrend = (
  data: TrafficVolumeTrendData | null
): TrafficVolumeTrendView | null => {
  if (!data?.times) return null;
  return {
    times: data.times.map((t) => isoTimeLabel(t)),
    series: toVolumeSeries(data.series ?? [], data.times.length),
  };
};

export const useSmartCityDashboard = ({
  siteId = 1,
  date,
  filterAbnormal = '1h',
  filterVolume = '1h',
  refetchIntervalMs = 30000,
}: UseSmartCityDashboardParams = {}) => {
  const hoursAbnormal = parseHours(filterAbnormal);
  const hoursVolume = parseHours(filterVolume);
  // Session language, in the 'eng' | 'ind' | 'kor' shape the api-dsh /event
  // endpoint expects - matches i18n.language directly (see SwitchButton.tsx).
  const { t, i18n } = useTranslation();
  const lang = i18n.language as 'eng' | 'ind' | 'kor';

  return useQueries({
    queries: [
      // 1. Live map POIs (PSC_PC0202001_BE_003). Ambulances move and incidents
      //    open/close, so this is the fast poll.
      {
        queryKey: ['smartCityPoi', siteId],
        queryFn: async () => {
          const res = await smartCityApi.getMapPoi({
            layers: 'incident,ambulance',
            // Default 'open' scope drops finished/rejected reports, but the
            // event list below shows every report registered today regardless
            // of status - 'today' keeps their markers around so clicking one
            // of those cards can still find a marker to focus on.
            incident_scope: 'today',
            // Only ambulances actively en route should plot as a POI -
            // standby/maintenance/offline vehicles aren't relevant to the
            // live map.
            ambulance_status: 'AMB_ST_ONGO',
          });
          return normalizeMarkers(res?.data ?? null, t);
        },
        refetchInterval: refetchIntervalMs,
        staleTime: 10000,
      },
      // 2. Static map POIs (PSC_PC0202001_BE_003, same endpoint). Hospitals and
      //    infrastructure change when one is built, so re-scanning the whole POI
      //    inventory at the live cadence is pure waste - hence the split.
      {
        queryKey: ['smartCityStaticPoi'],
        queryFn: async () => {
          const res = await smartCityApi.getMapPoi({
            layers: 'health_facility,infrastructure',
          });
          return normalizeMarkers(res?.data ?? null, t);
        },
        refetchInterval: refetchIntervalMs * 4,
        staleTime: 300000,
      },
      // 3. CCTV markers (GET /cctv via fetchCctvList)
      {
        queryKey: ['smartCityCctvList'],
        queryFn: async () => {
          const res = await fetchCctvList();
          return normalizeCctvMarkers(res?.data?.cctv_list ?? []);
        },
        refetchInterval: refetchIntervalMs * 4,
        staleTime: 300000,
      },
      // 4. Event List by Date (PSC_PC0201001_BE_003). City-wide and merged:
      //    anomaly windows plus PSC reports, so no site filter and no siteId in
      //    the query key. size: 'all' since the panel shows the whole day's
      //    list, not one page of it.
      {
        queryKey: ['smartCityEvents', date, lang],
        queryFn: async () => {
          if (!date) return [];
          const res = await smartCityApi.getEventList({
            date,
            size: 'all',
            lang,
          });
          return normalizeEvents(res?.data ?? null);
        },
        enabled: !!date,
        refetchInterval: refetchIntervalMs,
        staleTime: 10000,
      },
      // 4. Site Weather Details & Forecast
      {
        queryKey: ['smartCityWeather', siteId],
        queryFn: async () => {
          const res = await smartCityApi.getSiteWeather(siteId);
          return normalizeWeather(res?.data ?? null);
        },
        refetchInterval: refetchIntervalMs * 2,
        staleTime: 60000,
      },
      // 5. Anomaly Event Trend (PSC_PC0201001_BE_005). City-wide: no site filter,
      //    so the query key holds no siteId either.
      {
        queryKey: ['smartCityAnomalyTrend', hoursAbnormal],
        queryFn: async () => {
          const res = await smartCityApi.getAnomalyTrend({
            hours: hoursAbnormal,
          });
          return normalizeAnomalyTrend(res?.data ?? null);
        },
        refetchInterval: refetchIntervalMs,
        staleTime: 10000,
      },
      // 6. Traffic Volume Trend (PSC_PC0201001_BE_006). City-wide, same as above.
      {
        queryKey: ['smartCityVolumeTrend', hoursVolume],
        queryFn: async () => {
          const res = await smartCityApi.getTrafficVolumeTrend({
            hours: hoursVolume,
          });
          return normalizeVolumeTrend(res?.data ?? null);
        },
        refetchInterval: refetchIntervalMs,
        staleTime: 10000,
      },
    ],
    combine: ([
      poiQuery,
      staticPoiQuery,
      cctvQuery,
      eventsQuery,
      weatherQuery,
      anomalyQuery,
      volumeQuery,
    ]) => {
      // Merge the three POI sources into one flat marker list the page plots and
      // counts per toggle: cctv (GET /cctv), the static /map/poi layers
      // (health_facility/infrastructure) and the live ones (incident/ambulance).
      const cctvMarkers = (cctvQuery.data as MapMarker[]) || [];
      const staticMarkers = (staticPoiQuery.data as MapMarker[]) || [];
      const liveMarkers = (poiQuery.data as MapMarker[]) || [];
      return {
        poiData: [...cctvMarkers, ...staticMarkers, ...liveMarkers],
        events: (eventsQuery.data as EventCardItem[]) || [],
        weather: (weatherQuery.data as WeatherView) || null,
        anomalyTrend: (anomalyQuery.data as AnomalyTrendView) || null,
        volumeTrend: (volumeQuery.data as TrafficVolumeTrendView) || null,

        isPending:
          poiQuery.isPending ||
          staticPoiQuery.isPending ||
          eventsQuery.isPending ||
          weatherQuery.isPending ||
          anomalyQuery.isPending ||
          volumeQuery.isPending,

        isError:
          poiQuery.isError ||
          staticPoiQuery.isError ||
          cctvQuery.isError ||
          eventsQuery.isError ||
          weatherQuery.isError ||
          anomalyQuery.isError ||
          volumeQuery.isError,

        refetchAll: () => {
          poiQuery.refetch();
          staticPoiQuery.refetch();
          cctvQuery.refetch();
          eventsQuery.refetch();
          weatherQuery.refetch();
          anomalyQuery.refetch();
          volumeQuery.refetch();
        },
      };
    },
  });
};
